/**
 * ElecBid — CEC 2024 Voltage Drop Engine
 * Browning Electric
 *
 * Pure, dependency-free calculation engine implementing:
 *   - CEC 2024 (CSA C22.1:24) Rule 8-102 — Voltage drop (3% branch/feeder, 5% total,
 *     80% of OCPD rating when connected load unknown)
 *   - Appendix D, Table D3 — K values (ohms per circuit kilometre, 75 °C conductor
 *     operating temperature) for 2, 3, or 4 insulated conductors in cable or raceway
 *   - Table D3 Note 2 — voltage drop factor f by electrical system / circuit type
 *   - Table D3 Note 3 — ×0.95 for 60 °C and ×1.05 for 90 °C operating temperature
 *   - Table D3 Note 4 — ×0.86 for extra-low-voltage power circuits (load restricted
 *     to ≤ 2/3 of the copper 60 °C ampacity in Table 2)
 *   - Table 68 — maximum conductor lengths for 120 V dwelling general-use branch
 *     circuits (Rule 8-102 3) alternate compliance path)
 *   - Tables 2 & 4 — ampacity cross-check (≤3 conductors, 30 °C ambient).
 *     NOT applied: Table 5A ambient / Table 5C conductor-count derating, and
 *     Rule 14-104 2) OCPD caps (e.g. 14 AWG Cu limited to 15 A protection) —
 *     the engine sees load current only, never the breaker actually installed.
 *
 * Core formula (Table D3 Note 1):
 *   VD (volts) = K × f × I × L / 1000
 *     K = table K value, ohms per circuit kilometre (after temp multiplier,
 *         divided by number of parallel runs per phase)
 *     f = voltage drop factor from the system/circuit table (2 or 1.73)
 *     I = load current in amps (total per phase, across all parallel runs)
 *     L = one-way circuit length in metres
 *   VD% = VD / V × 100, where V is the circuit basis voltage for the chosen
 *         system/circuit row (line-to-line or line-to-grounded per the f table).
 *
 * This file is plain ESM with no imports so it can be loaded by both the CRA
 * webpack build (from .jsx) and Node directly (for the test suite in
 * scripts/test_voltage_drop.mjs).
 */

export const M_PER_FT = 0.3048; // exact by definition
export const FT_PER_M = 1 / M_PER_FT;

// ─── Table D3 — K values (Ω per circuit km, 75 °C operating temperature) ───────
// Columns per material: dc, pf100 (cable or raceway), cable90, cable80,
// raceway90, raceway80. "cable" includes manufactured multi-conductor
// assemblies (NMD90, TECK, AC90…); "raceway" = random single conductors pulled
// in raceway or laid flat and touching (T90/RW90 in EMT, PVC, rigid…).
// Aluminum is not listed for 14 AWG (null).
export const SIZES = [
  "14", "12", "10", "8", "6", "4", "3", "2", "1",
  "1/0", "2/0", "3/0", "4/0",
  "250", "300", "350", "400", "500", "600", "750", "1000",
];

export const K_TABLE = {
  "14":  { cu: { dc: 10.2,   pf100: 10.2,   cable90: 9.92,   cable80: 9.67,   raceway90: 10,     raceway80: 9.67   }, al: null },
  "12":  { cu: { dc: 6.38,   pf100: 6.38,   cable90: 6.25,   cable80: 6.1,    raceway90: 6.26,   raceway80: 6.11   },
           al: { dc: 10.5,   pf100: 10.5,   cable90: 10.3,   cable80: 10,     raceway90: 10.3,   raceway80: 9.99   } },
  "10":  { cu: { dc: 4.03,   pf100: 4.03,   cable90: 3.96,   cable80: 3.87,   raceway90: 3.96,   raceway80: 3.87   },
           al: { dc: 6.58,   pf100: 6.58,   cable90: 6.44,   cable80: 6.28,   raceway90: 6.45,   raceway80: 6.29   } },
  "8":   { cu: { dc: 2.54,   pf100: 2.54,   cable90: 2.5,    cable80: 2.45,   raceway90: 2.51,   raceway80: 2.45   },
           al: { dc: 4.14,   pf100: 4.14,   cable90: 4.07,   cable80: 3.97,   raceway90: 4.07,   raceway80: 3.98   } },
  "6":   { cu: { dc: 1.59,   pf100: 1.59,   cable90: 1.58,   cable80: 1.55,   raceway90: 1.58,   raceway80: 1.55   },
           al: { dc: 2.62,   pf100: 2.62,   cable90: 2.58,   cable80: 2.52,   raceway90: 2.58,   raceway80: 2.53   } },
  "4":   { cu: { dc: 1.01,   pf100: 1.01,   cable90: 1.01,   cable80: 0.987,  raceway90: 1.01,   raceway80: 1      },
           al: { dc: 1.65,   pf100: 1.65,   cable90: 1.63,   cable80: 1.6,    raceway90: 1.64,   raceway80: 1.61   } },
  "3":   { cu: { dc: 0.792,  pf100: 0.792,  cable90: 0.797,  cable80: 0.787,  raceway90: 0.801,  raceway80: 0.792  },
           al: { dc: 1.3,    pf100: 1.31,   cable90: 1.3,    cable80: 1.27,   raceway90: 1.3,    raceway80: 1.28   } },
  "2":   { cu: { dc: 0.626,  pf100: 0.627,  cable90: 0.636,  cable80: 0.629,  raceway90: 0.639,  raceway80: 0.635  },
           al: { dc: 1.04,   pf100: 1.04,   cable90: 1.04,   cable80: 1.02,   raceway90: 1.04,   raceway80: 1.03   } },
  "1":   { cu: { dc: 0.5,    pf100: 0.5,    cable90: 0.512,  cable80: 0.509,  raceway90: 0.516,  raceway80: 0.515  },
           al: { dc: 0.82,   pf100: 0.82,   cable90: 0.823,  cable80: 0.812,  raceway90: 0.827,  raceway80: 0.818  } },
  "1/0": { cu: { dc: 0.395,  pf100: 0.396,  cable90: 0.41,   cable80: 0.409,  raceway90: 0.414,  raceway80: 0.415  },
           al: { dc: 0.651,  pf100: 0.652,  cable90: 0.659,  cable80: 0.652,  raceway90: 0.663,  raceway80: 0.657  } },
  "2/0": { cu: { dc: 0.314,  pf100: 0.316,  cable90: 0.331,  cable80: 0.332,  raceway90: 0.335,  raceway80: 0.338  },
           al: { dc: 0.516,  pf100: 0.517,  cable90: 0.526,  cable80: 0.522,  raceway90: 0.53,   raceway80: 0.528  } },
  "3/0": { cu: { dc: 0.249,  pf100: 0.251,  cable90: 0.267,  cable80: 0.27,   raceway90: 0.271,  raceway80: 0.275  },
           al: { dc: 0.408,  pf100: 0.409,  cable90: 0.42,   cable80: 0.419,  raceway90: 0.424,  raceway80: 0.425  } },
  "4/0": { cu: { dc: 0.197,  pf100: 0.2,    cable90: 0.217,  cable80: 0.221,  raceway90: 0.221,  raceway80: 0.226  },
           al: { dc: 0.326,  pf100: 0.327,  cable90: 0.341,  cable80: 0.341,  raceway90: 0.345,  raceway80: 0.347  } },
  "250": { cu: { dc: 0.167,  pf100: 0.171,  cable90: 0.188,  cable80: 0.193,  raceway90: 0.192,  raceway80: 0.198  },
           al: { dc: 0.275,  pf100: 0.277,  cable90: 0.291,  cable80: 0.293,  raceway90: 0.295,  raceway80: 0.299  } },
  "300": { cu: { dc: 0.14,   pf100: 0.144,  cable90: 0.162,  cable80: 0.167,  raceway90: 0.166,  raceway80: 0.172  },
           al: { dc: 0.229,  pf100: 0.231,  cable90: 0.247,  cable80: 0.249,  raceway90: 0.25,   raceway80: 0.255  } },
  "350": { cu: { dc: 0.12,   pf100: 0.125,  cable90: 0.143,  cable80: 0.148,  raceway90: 0.147,  raceway80: 0.154  },
           al: { dc: 0.196,  pf100: 0.199,  cable90: 0.215,  cable80: 0.218,  raceway90: 0.219,  raceway80: 0.224  } },
  "400": { cu: { dc: 0.105,  pf100: 0.111,  cable90: 0.129,  cable80: 0.135,  raceway90: 0.133,  raceway80: 0.14   },
           al: { dc: 0.172,  pf100: 0.175,  cable90: 0.191,  cable80: 0.195,  raceway90: 0.195,  raceway80: 0.201  } },
  "500": { cu: { dc: 0.0836, pf100: 0.0912, cable90: 0.11,   cable80: 0.116,  raceway90: 0.114,  raceway80: 0.121  },
           al: { dc: 0.138,  pf100: 0.141,  cable90: 0.158,  cable80: 0.163,  raceway90: 0.162,  raceway80: 0.168  } },
  "600": { cu: { dc: 0.0697, pf100: 0.0785, cable90: 0.0969, cable80: 0.104,  raceway90: 0.101,  raceway80: 0.109  },
           al: { dc: 0.115,  pf100: 0.119,  cable90: 0.136,  cable80: 0.142,  raceway90: 0.14,   raceway80: 0.147  } },
  "750": { cu: { dc: 0.0558, pf100: 0.0668, cable90: 0.085,  cable80: 0.0915, raceway90: 0.0889, raceway80: 0.097  },
           al: { dc: 0.0916, pf100: 0.0968, cable90: 0.115,  cable80: 0.121,  raceway90: 0.119,  raceway80: 0.126  } },
  "1000":{ cu: { dc: 0.0417, pf100: 0.0558, cable90: 0.0739, cable80: 0.0805, raceway90: 0.0778, raceway80: 0.086  },
           al: { dc: 0.0686, pf100: 0.0758, cable90: 0.0933, cable80: 0.0994, raceway90: 0.0973, raceway80: 0.105  } },
};

// ─── Table D3 Note 2 — voltage drop factor f by system / circuit ───────────────
// Transcribed in code-book order. `basis` names the voltage VD% is measured
// against ("line-to-line" or "line-to-grounded" / DC equivalents), exactly as
// printed in the table. `phase` gates which pf columns apply (dc vs ac) and how
// current is derived from watts (√3 only where f = 1.73).
export const F_TABLE = [
  { id: "dc_2w_pn",   system: "DC",         circuit: "2-wire, positive-to-negative",                  f: 2,    basis: "positive-to-negative",         phase: "dc" },
  { id: "dc_2w_pg",   system: "DC",         circuit: "2-wire, positive-to-grounded conductor",        f: 2,    basis: "positive-to-grounded",         phase: "dc" },
  { id: "dc_2w_ng",   system: "DC",         circuit: "2-wire, negative-to-grounded conductor",        f: 2,    basis: "negative-to-grounded",         phase: "dc" },
  { id: "dc_3w_ll",   system: "DC",         circuit: "3-wire, line-to-line with grounded conductor",  f: 2,    basis: "line-to-line",                 phase: "dc" },
  { id: "sp_2w_lg",   system: "1-phase AC", circuit: "2-wire, line-to-grounded conductor",            f: 2,    basis: "line-to-grounded conductor",   phase: "1ac" },
  { id: "sp_2w_ll",   system: "1-phase AC", circuit: "2-wire, line-to-line",                          f: 2,    basis: "line-to-line",                 phase: "1ac" },
  { id: "sp_3w_ll",   system: "1-phase AC", circuit: "3-wire, line-to-line, with grounded conductor", f: 2,    basis: "line-to-line",                 phase: "1ac" },
  { id: "tp_2w_lg",   system: "3-phase AC", circuit: "2-wire, line-to-grounded conductor",            f: 2,    basis: "line-to-grounded conductor",   phase: "1ac" },
  { id: "tp_2w_ll",   system: "3-phase AC", circuit: "2-wire, line-to-line, no grounded conductor",   f: 2,    basis: "line-to-line",                 phase: "1ac" },
  { id: "tp_3w_ll_g", system: "3-phase AC", circuit: "3-wire, line-to-line, with grounded conductor", f: 2,    basis: "line-to-line",                 phase: "1ac" },
  { id: "tp_3w_lg",   system: "3-phase AC", circuit: "3-wire, line-to-grounded conductor",            f: 2,    basis: "line-to-grounded conductor",   phase: "1ac" },
  { id: "tp_3w_ll",   system: "3-phase AC", circuit: "3-wire, line-to-line, no grounded conductor",   f: 1.73, basis: "line-to-line",                 phase: "3ac" },
  { id: "tp_4w_ll",   system: "3-phase AC", circuit: "4-wire, line-to-line, with grounded conductor", f: 1.73, basis: "line-to-line",                 phase: "3ac" },
];

export function getFRow(fId) {
  return F_TABLE.find(r => r.id === fId) || null;
}

// ─── Table D3 Notes 3 & 4 — operating temperature multipliers ──────────────────
// Base table is 75 °C. Note 3: ×0.95 @ 60 °C, ×1.05 @ 90 °C. Note 4
// ("Notwithstanding Note 3"): ×0.86 for extra-low-voltage power circuits whose
// load does not exceed 2/3 of the copper 60 °C ampacity in Table 2.
export const TEMP_MULTIPLIER = { 60: 0.95, 75: 1, 90: 1.05 };
export const ELV_MULTIPLIER = 0.86;
// Note 4's operative term is the Section 0 "extra-low-voltage power circuit":
// operates at not more than 30 V, supply limited to 1000 V·A, and not a
// Class 2 circuit. Only the 30 V bound is checkable here — the elv flag is
// otherwise the caller's assertion. (Distinct from the broader "extra-low
// voltage" CLASS, which is ≤30 V ac / ≤60 V dc.)
export const ELV_MAX_VOLTAGE = 30;

// ─── Table 68 — dwelling-unit 120 V general-use branch circuits ────────────────
// Rule 8-102 3): 90 °C copper conductors, 30 °C ambient, length measured from
// the SUPPLY SIDE OF THE CONSUMER'S SERVICE to the furthest point of
// utilization. An empirical alternate compliance path — NOT derivable from the
// Table D3 formula. Does not apply to appliance/kitchen/heating/dedicated
// receptacle circuits (see Appendix B note).
export const TABLE_68 = {
  "14": { 15: 38, 20: null },
  "12": { 15: 60, 20: 50 },
  "10": { 15: 96, 20: 78 },
};

// ─── Tables 2 & 4 — ampacity (≤3 conductors in raceway/cable, 30 °C ambient) ──
// Columns: 60 / 75 / 90 °C insulation temperature rating.
export const AMPACITY = {
  cu: {
    "14": { 60: 15,  75: 20,  90: 25  },
    "12": { 60: 20,  75: 25,  90: 30  },
    "10": { 60: 30,  75: 35,  90: 40  },
    "8":  { 60: 40,  75: 50,  90: 55  },
    "6":  { 60: 55,  75: 65,  90: 75  },
    "4":  { 60: 70,  75: 85,  90: 95  },
    "3":  { 60: 85,  75: 100, 90: 115 },
    "2":  { 60: 95,  75: 115, 90: 130 },
    "1":  { 60: 110, 75: 130, 90: 145 },
    "1/0":{ 60: 125, 75: 150, 90: 170 },
    "2/0":{ 60: 145, 75: 175, 90: 195 },
    "3/0":{ 60: 165, 75: 200, 90: 225 },
    "4/0":{ 60: 195, 75: 230, 90: 260 },
    "250":{ 60: 215, 75: 255, 90: 290 },
    "300":{ 60: 240, 75: 285, 90: 320 },
    "350":{ 60: 260, 75: 310, 90: 350 },
    "400":{ 60: 280, 75: 335, 90: 380 },
    "500":{ 60: 320, 75: 380, 90: 430 },
    "600":{ 60: 350, 75: 420, 90: 475 },
    "750":{ 60: 400, 75: 475, 90: 535 },
    "1000":{60: 455, 75: 545, 90: 615 },
  },
  al: {
    "12": { 60: 15,  75: 20,  90: 25  },
    "10": { 60: 25,  75: 30,  90: 35  },
    "8":  { 60: 35,  75: 40,  90: 45  },
    "6":  { 60: 40,  75: 50,  90: 55  },
    "4":  { 60: 55,  75: 65,  90: 75  },
    "3":  { 60: 65,  75: 75,  90: 85  },
    "2":  { 60: 75,  75: 90,  90: 100 },
    "1":  { 60: 85,  75: 100, 90: 115 },
    "1/0":{ 60: 100, 75: 120, 90: 135 },
    "2/0":{ 60: 115, 75: 135, 90: 150 },
    "3/0":{ 60: 130, 75: 155, 90: 175 },
    "4/0":{ 60: 150, 75: 180, 90: 205 },
    "250":{ 60: 170, 75: 205, 90: 230 },
    "300":{ 60: 195, 75: 230, 90: 260 },
    "350":{ 60: 210, 75: 250, 90: 280 },
    "400":{ 60: 225, 75: 270, 90: 305 },
    "500":{ 60: 260, 75: 310, 90: 350 },
    "600":{ 60: 285, 75: 340, 90: 385 },
    "750":{ 60: 320, 75: 385, 90: 435 },
    "1000":{60: 375, 75: 445, 90: 500 },
  },
};

// Rule 12-108 1): conductors run in parallel must be 1/0 AWG or larger
// (limited smaller-size exceptions exist for control/instrumentation).
const PARALLEL_MIN_SIZE_INDEX = SIZES.indexOf("1/0");

// ─── K lookup ──────────────────────────────────────────────────────────────────
// pf: "dc" | "100" | "90" | "80". install matters only at 90/80 pf — the DC and
// 100% pf columns are a single "cable or raceway" value in Table D3.
export function kColumnName(pf, install) {
  if (pf === "dc") return "dc";
  if (pf === "100") return "pf100";
  if (pf === "90" || pf === "80") {
    if (install === "raceway") return `raceway${pf}`;
    if (install === "cable") return `cable${pf}`;
    return null; // unknown install must not silently fall back to cable
  }
  return null;
}

export function lookupK({ material, size, pf, install }) {
  if (material !== "cu" && material !== "al") return { error: `Unknown conductor material "${material}".` };
  const row = K_TABLE[size];
  if (!row) return { error: `Size ${size} is not listed in Table D3.` };
  const mat = row[material];
  if (!mat) return { error: `${size} AWG aluminum is not listed in Table D3 — smallest aluminum size is 12 AWG.` };
  const col = kColumnName(pf, install);
  if (!col) return { error: `Unknown power factor / installation combination "${pf}" / "${install}".` };
  return { k: mat[col], column: col };
}

// ─── Current helpers ───────────────────────────────────────────────────────────
// Rule 8-102 1): if connected load is unknown, use 80% of the OCPD rating.
export function currentFromBreaker(breakerAmps) {
  return (breakerAmps || 0) * 0.8;
}

// Watts → amps using the basis voltage of the chosen circuit row. √3 (printed
// as 1.73 in Table D3) applies only to balanced 3-phase rows (f = 1.73);
// 2-pole and line-to-neutral circuits are I = W / V (Rule 8-100 divisors).
export function currentFromWatts(watts, voltage, fId) {
  const fRow = getFRow(fId);
  if (!fRow || !voltage) return 0;
  if (fRow.phase === "3ac") return (watts || 0) / (1.73 * voltage);
  return (watts || 0) / voltage;
}

// ─── Core calculation ─────────────────────────────────────────────────────────
/**
 * calcVoltageDrop(params) → result
 *
 * params:
 *   material  "cu" | "al"
 *   size      one of SIZES
 *   install   "cable" | "raceway"
 *   pf        "dc" | "100" | "90" | "80"  (must be "dc" iff the circuit is DC)
 *   fId       id from F_TABLE
 *   voltage   basis voltage for the chosen circuit row (volts)
 *   current   total load current per phase, amps (across all parallel runs)
 *   lengthM   one-way circuit length, metres
 *   temp      60 | 75 | 90 (conductor OPERATING temperature; Table D3 base 75)
 *   elv       true → apply Note 4 ×0.86 (extra-low-voltage power circuit)
 *   parallel  parallel conductors per phase (default 1)
 *   limitPct  allowed VD% (3 = branch/feeder, 5 = total; Rule 8-102 1))
 */
export function calcVoltageDrop(params) {
  const {
    material, size, install, pf, fId,
    voltage, current, lengthM,
    temp = 75, elv = false, parallel = 1, limitPct = 3,
  } = params;

  const warnings = [];

  const fRow = getFRow(fId);
  if (!fRow) return { ok: false, error: "Select a system / circuit type." };
  if (fRow.phase === "dc" && pf !== "dc")
    return { ok: false, error: "DC circuits use the DC column of Table D3 — power factor does not apply." };
  if (fRow.phase !== "dc" && pf === "dc")
    return { ok: false, error: "AC circuits require a power factor column (100 / 90 / 80%)." };

  if (!(voltage > 0)) return { ok: false, error: "Enter the circuit voltage." };
  if (!(current > 0)) return { ok: false, error: "Enter the load current." };
  if (!(lengthM > 0)) return { ok: false, error: "Enter the run length." };
  const nPar = Math.max(1, Math.floor(parallel || 1));
  if (!(limitPct > 0)) return { ok: false, error: "Enter a voltage drop limit." };

  const kRes = lookupK({ material, size, pf, install });
  if (kRes.error) return { ok: false, error: kRes.error };

  // Temperature multiplier — Note 4 overrides Note 3 when it legitimately
  // applies (ELV power circuit + load within 2/3 of Cu 60 °C Table 2 ampacity).
  const tempC = Number(temp);
  let multiplier = TEMP_MULTIPLIER[tempC];
  let multiplierNote = tempC === 75 ? "Table D3 base (75 °C)" : `Note 3 — ${tempC} °C operating (×${multiplier})`;
  if (multiplier === undefined) return { ok: false, error: "Operating temperature must be 60, 75, or 90 °C." };

  let elvApplied = false;
  if (elv) {
    if (voltage > ELV_MAX_VOLTAGE) {
      warnings.push(`Note 4 (×0.86) applies only to extra-low-voltage power circuits (≤ ${ELV_MAX_VOLTAGE} V) — using the ${tempC} °C multiplier instead.`);
    } else {
      const cuAmp = AMPACITY.cu[size] ? AMPACITY.cu[size][60] : null;
      const perRun = current / nPar;
      if (material !== "cu") {
        // Deliberately stricter than the printed note: Note 4's 2/3-of-copper-
        // ampacity load cap exists to hold the conductor near 40 °C (Note 6),
        // which the copper benchmark does not guarantee for an aluminum
        // conductor of the same size. Refusing ×0.86 here is conservative —
        // overstates VD by up to ~16% vs the 75 °C base (~10% vs the 60 °C
        // multiplier) — and is surfaced as a warning.
        warnings.push("Note 4 (×0.86) is defined for copper conductors — using the standard temperature multiplier instead.");
      } else if (cuAmp !== null && perRun > (2 / 3) * cuAmp) {
        warnings.push(`Note 4 (×0.86) requires load ≤ 2/3 of the copper 60 °C ampacity (${((2 / 3) * cuAmp).toFixed(1)} A for ${labelForSize(size)}) — using the ${tempC} °C multiplier instead.`);
      } else {
        multiplier = ELV_MULTIPLIER;
        multiplierNote = "Note 4 — extra-low-voltage power circuit (×0.86)";
        elvApplied = true;
      }
    }
  }

  const kEff = (kRes.k * multiplier) / nPar;
  const vdVolts = (kEff * fRow.f * current * lengthM) / 1000;
  const vdPct = (vdVolts / voltage) * 100;
  const pass = vdPct <= limitPct + 1e-9;
  const voltsAtLoad = voltage - vdVolts;

  // Max one-way length for this conductor at the limit.
  const maxLengthM = (limitPct / 100) * voltage * 1000 / (kEff * fRow.f * current);

  // Ampacity cross-check — Table 2 / Table 4, ≤3 conductors, 30 °C ambient.
  // Insulation rating column matches the operating-temperature selection.
  const ampRow = AMPACITY[material][size];
  const ampacity = ampRow ? ampRow[tempC] : null;
  const perRunCurrent = current / nPar;
  const ampacityExceeded = ampacity !== null && perRunCurrent > ampacity + 1e-9;
  if (ampacityExceeded) {
    warnings.push(`${perRunCurrent.toFixed(1)} A per conductor exceeds the Table ${material === "cu" ? "2" : "4"} ${tempC} °C ampacity of ${ampacity} A for ${labelForSize(size)} ${material === "cu" ? "copper" : "aluminum"}.`);
  }

  if (nPar > 1 && SIZES.indexOf(size) < PARALLEL_MIN_SIZE_INDEX) {
    warnings.push("Rule 12-108: conductors run in parallel must be 1/0 AWG or larger.");
  }
  if (nPar > 1) {
    warnings.push("Ampacity check assumes ≤3 current-carrying conductors per cable/raceway (Tables 2/4) — parallel sets sharing one raceway need Table 5C derating, which is not applied here.");
  }

  return {
    ok: true,
    kBase: kRes.k,
    kColumn: kRes.column,
    multiplier,
    multiplierNote,
    elvApplied,
    kEff,
    f: fRow.f,
    fRow,
    vdVolts,
    vdPct,
    pass,
    voltsAtLoad,
    maxLengthM,
    ampacity,
    ampacityTemp: tempC,
    ampacityExceeded,
    perRunCurrent,
    parallel: nPar,
    warnings,
  };
}

// ─── Minimum conductor size that meets the limit ───────────────────────────────
// Scans Table D3 sizes small→large. Returns the first size whose VD% is within
// the limit (minSizeVd) and the first that also passes the ampacity check
// (minSizeBoth). Either is null if no listed size qualifies. When the params
// call for parallel runs, sizes below 1/0 AWG are skipped entirely —
// Rule 12-108 1) does not permit them in parallel sets.
export function minSizeForLimit(params) {
  const nPar = Math.max(1, Math.floor(params.parallel || 1));
  let minSizeVd = null;
  let minSizeBoth = null;
  for (const size of SIZES) {
    if (nPar > 1 && SIZES.indexOf(size) < PARALLEL_MIN_SIZE_INDEX) continue;
    const res = calcVoltageDrop({ ...params, size });
    if (!res.ok) continue; // e.g. 14 AWG aluminum
    if (res.pass && minSizeVd === null) minSizeVd = size;
    if (res.pass && !res.ampacityExceeded && minSizeBoth === null) {
      minSizeBoth = size;
      break;
    }
  }
  return { minSizeVd, minSizeBoth };
}

// ─── Table 68 lookup (Rule 8-102 3) alternate path) ────────────────────────────
// Raw table lookup ONLY — returns null for sizes/breakers outside the table,
// but does NOT enforce the rule's scope conditions (dwelling-unit general-use
// branch circuit, 120 V 1φ, 90 °C copper, length measured from the supply side
// of the consumer's service, and the Appendix B exclusions for appliance/
// kitchen/heating/dedicated circuits). Callers must gate on those themselves,
// as VoltageDropCalc.jsx does.
export function table68Length(size, breakerAmps) {
  const row = TABLE_68[size];
  if (!row) return null;
  const v = row[breakerAmps];
  return v === undefined ? null : v;
}

// ─── Formatting helpers ────────────────────────────────────────────────────────
export function labelForSize(size) {
  return /^\d+$/.test(size) && Number(size) >= 250 ? `${size} kcmil` : `${size} AWG`;
}

export function metresToFeet(m) { return m * FT_PER_M; }
export function feetToMetres(ft) { return ft * M_PER_FT; }
